import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import {
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Editor } from '@tiptap/core';
import {
  LooksOne as Heading1Icon,
  LooksTwo as Heading2Icon,
  Looks3 as Heading3Icon,
  FormatListBulleted as BulletListIcon,
  FormatListNumbered as NumberedListIcon,
  CheckBox as TaskListIcon,
  FormatQuote as QuoteIcon,
  Code as CodeIcon,
  HorizontalRule as DividerIcon,
  TableChart as TableIcon,
} from '@mui/icons-material';
import { NoteAddIcon } from 'components/SVG';
import { editorCommands } from '../utils/editorCommands';

export interface CommandItem {
  title: string;
  description: string;
  icon: React.ReactNode;
  command: (props: { editor: Editor }) => boolean | void;
}

export interface SlashCommandsListProps {
  items: CommandItem[];
  command: (item: CommandItem) => void;
  editor: Editor;
  range?: { from: number; to: number };
}

const SlashCommandsList = forwardRef<
  { onKeyDown: (props: { event: KeyboardEvent }) => boolean },
  SlashCommandsListProps
>((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [commandItems, setCommandItems] = useState<CommandItem[]>([]);

  const selectItem = (index: number) => {
    const item = commandItems[index];

    if (item) {
      props.command(item);
    }
  };

  const upHandler = () => {
    setSelectedIndex(
      (selectedIndex + commandItems.length - 1) % commandItems.length
    );
  };

  const downHandler = () => {
    setSelectedIndex((selectedIndex + 1) % commandItems.length);
  };

  const enterHandler = () => {
    selectItem(selectedIndex);
  };

  useEffect(() => {
    if (props.items) {
      setCommandItems(props.items);
    }
  }, [props.items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (event.key === 'ArrowUp') {
        upHandler();
        return true;
      }

      if (event.key === 'ArrowDown') {
        downHandler();
        return true;
      }

      if (event.key === 'Enter') {
        enterHandler();
        return true;
      }

      return false;
    },
  }));

  return (
    <Paper elevation={3} sx={{ width: 320, maxHeight: 400, overflow: 'auto' }}>
      <List dense>
        {commandItems.map((item, index) => (
          <ListItem
            key={index}
            button
            selected={index === selectedIndex}
            onClick={() => selectItem(index)}
            sx={{ '&.Mui-selected': { backgroundColor: '#e3f2fd' } }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.title}
              secondary={item.description}
              primaryTypographyProps={{
                sx: { fontWeight: index === selectedIndex ? 'bold' : 'normal' },
              }}
            />
          </ListItem>
        ))}
      </List>
    </Paper>
  );
});

export const defaultItems: CommandItem[] = [
  {
    title: 'Heading 1',
    description: 'Large title text',
    icon: <Heading1Icon />,
    command: ({ editor }) => editorCommands.toggleHeading1(editor),
  },
  {
    title: 'Heading 2',
    description: 'Medium title text',
    icon: <Heading2Icon />,
    command: ({ editor }) => editorCommands.toggleHeading2(editor),
  },
  {
    title: 'Heading 3',
    description: 'Small title text',
    icon: <Heading3Icon />,
    command: ({ editor }) => editorCommands.toggleHeading3(editor),
  },
  {
    title: 'Bullet List',
    description: 'Create a bulleted list',
    icon: <BulletListIcon />,
    command: ({ editor }) => editorCommands.toggleBulletList(editor),
  },
  {
    title: 'Numbered List',
    description: 'Create a numbered list',
    icon: <NumberedListIcon />,
    command: ({ editor }) => editorCommands.toggleOrderedList(editor),
  },
  {
    title: 'Task List',
    description: 'Create a task list with checkboxes',
    icon: <TaskListIcon />,
    command: ({ editor }) => editorCommands.toggleTaskList(editor),
  },
  {
    title: 'Blockquote',
    description: 'Create a blockquote',
    icon: <QuoteIcon />,
    command: ({ editor }) => editorCommands.toggleBlockquote(editor),
  },
  {
    title: 'Code Block',
    description: 'Create a code block',
    icon: <CodeIcon />,
    command: ({ editor }) => editorCommands.toggleCodeBlock(editor),
  },
  {
    title: 'Divider',
    description: 'Insert a horizontal divider',
    icon: <DividerIcon />,
    command: ({ editor }) => editorCommands.setHorizontalRule(editor),
  },
  {
    title: 'Table',
    description: 'Insert a table (3x3)',
    icon: <TableIcon />,
    command: ({ editor }) => {
      if (
        editor &&
        editor.commands &&
        typeof editor.commands.insertTable === 'function'
      ) {
        return editor.commands.insertTable({
          rows: 3,
          cols: 3,
          withHeaderRow: true,
        });
      } else {
        console.error('insertTable command not available');
        return false;
      }
    },
  },
  {
    title: 'Insert Reference',
    description: 'Insert a reference to another step',
    icon: <NoteAddIcon width='20px' height='20px' />,
    command: () => {
      // This will be overridden in the main component
      return false;
    },
  },
];

export default SlashCommandsList;
